import * as XLSX from 'xlsx';

/**
 * A "Hold listed items" rule's AS400 items, from the editor's text box or from
 * a workbook (a Division's sensitive list: one column of items, no header).
 */

/** Trimmed, blanks dropped, first occurrence kept. */
function distinct(items: Iterable<string>): string[] {
  const seen = new Set<string>();
  for (const item of items) {
    const trimmed = item.trim();
    if (trimmed !== '') seen.add(trimmed);
  }
  return [...seen];
}

/** Items typed or pasted one per line, or separated by commas, semicolons or tabs. */
export function parseItemList(text: string): string[] {
  return distinct(text.split(/[\r\n,;\t]+/));
}

/** Both lists, de-duplicated, in order: what an import adds to the text box. */
export function mergeItemLists(current: string[], added: string[]): string[] {
  return distinct([...current, ...added]);
}

function cellText(value: unknown): string {
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return '';
}

/**
 * A first cell like "AS400 ITEM" or "Item #" is a header, not an item: AS400
 * items (HUBHC6RPEB) have no spaces and do not spell ITEM.
 */
function looksLikeHeader(text: string): boolean {
  return /\s/.test(text) || /item/i.test(text);
}

/** The first column's items; a header-like first row is skipped. */
export function itemsFromRows(rows: readonly unknown[][]): string[] {
  const column = rows.flatMap((row) => {
    const text = cellText(row[0]);
    return text === '' ? [] : [text];
  });
  const first = column[0];
  const items =
    first !== undefined && looksLikeHeader(first) ? column.slice(1) : column;
  return distinct(items);
}

/** The items in the first column of the workbook's first sheet. */
export function readItemListWorkbook(data: ArrayBuffer): string[] {
  const workbook = XLSX.read(data, { type: 'array', sheets: 0 });
  const firstName = workbook.SheetNames[0];
  const sheet =
    firstName === undefined ? undefined : workbook.Sheets[firstName];
  if (!sheet) return [];
  const rows = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
    header: 1,
    raw: true,
    blankrows: false,
  });
  return itemsFromRows(rows);
}
